import React, { useEffect } from 'react';
import Pagination from 'components/ui/pagination';

import OptionsFilters, {
  FILTER_KEYS,
  type FilterKey,
  type FilterOptions,
  type Filters
} from './document_index/options_filters';
import DocTemplateTable from './document_index/doc_template_table';
import TemplateAccessReportModal from './template_access_report_modal';

import { deleteRequest, get, post, put } from 'helpers/api';
import confirm from 'components/confirm';
import DuplicateModal from './duplicate_modal';
import type { DocTemplate, SortDirection, SortState } from './document_index/types';

type DocTemplateListProps = {
  apiEndpoint: string,
  duplicateTemplateUrl?: string,
  teamsAccess?: boolean,
};

type DuplicateData = {
  show: boolean,
  template: DocTemplate | Record<string, never>,
};

export default function DocTemplateList({
  apiEndpoint,
  duplicateTemplateUrl,
  teamsAccess
}: DocTemplateListProps) {
  const [documentTemplates, setDocumentTemplates] = React.useState<DocTemplate[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [searchText, setSearchText] = React.useState('');
  const [filters, setFilters] = React.useState<Filters>(
    () => FILTER_KEYS.reduce((all, key) => ({ ...all, [key]: [] }), {} as Filters)
  );
  const [duplicateData, setDuplicateData] = React.useState<DuplicateData>({ show: false, template: {} });
  const [duplicateSaveError, setDuplicateSaveError] = React.useState<string | null>(null);
  const [sortState, setSortState] = React.useState<SortState>({ name: 'name', direction: 'asc' });
  const [templateReport, setTemplateReport] = React.useState<DocTemplate | null>(null);

  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [filterOptions, setFilterOptions] = React.useState<FilterOptions>({});
  const [debouncedSearchText, setDebouncedSearchText] = React.useState('');
  const [teamsEnabled, setTeamsEnabled] = React.useState(false);

  // Filters can change while a request is still out, and responses don't have to come back in order. Only the
  // newest one is allowed to land, so an older, slower response can't replace the current results.
  const latestRequest = React.useRef(0);

  const fetchTemplates = React.useCallback(async () => {
    const request = ++latestRequest.current;
    setLoading(true);
    try {
      const response = await get(apiEndpoint, {
        params: {
          page: currentPage,
          search: debouncedSearchText || undefined,
          ...FILTER_KEYS.reduce(
            (all, key) => ({ ...all, [key]: filters[key].length > 0 ? filters[key] : undefined }),
            {}
          ),
          sortBy: sortState.name,
          sortDirection: sortState.direction
        }
      });
      if (request !== latestRequest.current) return;

      setDocumentTemplates(response.records);
      setTeamsEnabled(Boolean(response.teamsEnabled));

      if (response.meta) {
        setTotalPages(response.meta.totalPages);
      }

      if (response.filterOptions) {
        const options = response.filterOptions;
        setFilterOptions({
          documentType: options.documentTypes,
          availability: options.availability,
          propertyType: options.propertyTypes,
          usersAndTeams: options.usersAndTeams,
          tags: options.tags,
          createdBy: options.createdBy
        });
      }
    } catch (error) {
      if (request !== latestRequest.current) return;

      console.error('Error fetching templates:', error);
      setDocumentTemplates([]);
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  }, [apiEndpoint, currentPage, debouncedSearchText, filters, sortState]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchText !== debouncedSearchText) {
        setDebouncedSearchText(searchText);
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, debouncedSearchText]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleUpdateSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleSearchClear = () => {
    setSearchText('');
    setCurrentPage(1);
  };

  const handleSort = (sortKey: string, direction: SortDirection) => {
    setSortState({ name: sortKey, direction });
    setCurrentPage(1);
  };

  const handleFilterChange = (key: FilterKey, values: string[]) => {
    setFilters(current => ({ ...current, [key]: values }));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDelete = (template: DocTemplate) => {
    const message = template.createdByName ?
      `Are you sure you want to delete this template created by ${template.createdByName}?` :
      'Are you sure?';

    confirm(message, {
      buttonLabel: 'Delete Document Template',
      buttonStyle: 'danger',
      title: `Delete "${template.name}"?`
    }).then(() => {
      deleteRequest(template.deletePath as string).then(() => {
        fetchTemplates();
      });
    }).catch(() => {
      // noop
    });
  };

  const handleSetDefault = async (template: DocTemplate, transactionType: 'sale' | 'lease', enabled: boolean) => {
    try {
      await put(template.setDefaultProposalPath, { transactionType, enabled });
      fetchTemplates();
    } catch (error) {
      console.error('Error updating default proposal template:', error);
    }
  };

  const handleDuplicateOpen = (template: DocTemplate) => {
    setDuplicateData({ show: true, template });
  };
  const handleDuplicateClose = () => {
    setDuplicateData({ show: false, template: {} });
    setDuplicateSaveError(null);
  };
  const handleDuplicateSave = ({ duplicateId, newName }: { duplicateId: number, newName: string }) => {
    post(duplicateTemplateUrl, { duplicateId, newName }).then((resp) => {
      if (resp.success) {
        handleDuplicateClose();
        window.location.href = resp.editPath;
      } else {
        setDuplicateSaveError('Sorry, something went wrong when duplicating');
      }
    }).catch((e) => {
      setDuplicateSaveError(e || 'Sorry, an unexpected error occurred when duplicating');
    });
  };

  const pageHeader = <h4 className="mb-4">Document Defaults</h4>;

  if (loading) {
    return (
      <div>
        {pageHeader}
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {pageHeader}
      <OptionsFilters
        filterOptions={filterOptions}
        filters={filters}
        searchText={searchText}
        teamsEnabled={teamsEnabled}
        onFilterChange={handleFilterChange}
        onSearchChange={handleUpdateSearchText}
        onSearchClear={handleSearchClear}
      />

      {documentTemplates.length > 0 ? (
        <div>
          <DocTemplateTable
            sortState={sortState}
            teamsEnabled={teamsEnabled}
            templates={documentTemplates}
            onAccessTemplateReport={teamsAccess ? (template => setTemplateReport(template)) : undefined}
            onDelete={handleDelete}
            onDuplicate={handleDuplicateOpen}
            onSetDefault={handleSetDefault}
            onSort={handleSort}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onChange={handlePageChange}
          />

          <DuplicateModal
            existingTemplates={documentTemplates}
            saveErrorMessage={duplicateSaveError}
            show={duplicateData.show}
            template={duplicateData.template}
            onHide={handleDuplicateClose}
            onSave={handleDuplicateSave}
          />

          {templateReport && (
            <TemplateAccessReportModal
              documentTemplate={templateReport}
              onHide={() => setTemplateReport(null)}
            />
          )}
        </div>
      ) : (
        <h6 className="mt-7">No Documents Found</h6>
      )}
    </div>
  );
}
